
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Flex } from "antd";
import { ColumnsType } from "antd/es/table";
import { DollarOutlined, EnvironmentOutlined } from "@ant-design/icons";

import { CreditLevelSelect, CurrencySelect, CountrySelect, DepartmentSelect } from "@/components/Selector";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { Customer } from "@/types/sales";
import SText from "@/styles/SText";
import IconFolder from "@/assets/images/fileIcon/folder.svg";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";

type UseCustomerColumn = (props: {
  onDetail: (params: Customer | null) => void;
  onDel: (id: string) => void;
  onOpenTab: (type: 'price' | 'shipAddress' | 'doc', item: Customer) => void;
}) => ColumnsType<Customer>

const useCustomerColumn: UseCustomerColumn = ({ onDetail, onDel, onOpenTab }) => {
  const { t } = useTranslation();

  const columns: ColumnsType<Customer> = useMemo(() => [
    {
      title: <SText text={t("sale.code")} />,
      dataIndex: "code",
      width: 100,
      fixed: "left",
      sorter: (a, b) => {
        const valA = a.code ?? "";
        const valB = b.code ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, item) => <SText text={item.code} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.principal")} />,
      dataIndex: "principal",
      width: 100,
      render: (val, item) => (
        <SText text={item.principal ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.name1")} />,
      dataIndex: "name1",
      width: 140,
      render: (_, item) => (
        <SText text={item.name1 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.name2")} />,
      dataIndex: "name2",
      width: 140,
      render: (_, item) => (
        <SText text={item.name2 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.shortName")} />,
      dataIndex: "shortName",
      width: 100,
      render: (_, item) => (
        <SText text={item.shortName ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.customerType")} />,
      dataIndex: "type",
      width: 120,
      render: (_, item) => (
        <Dict value={item.type ?? ""} type={DictEnum.SALE_CUSTOMER_TYPE} readOnly style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.country")} />,
      dataIndex: "country",
      width: 100,
      render: (val, item) => (
        <CountrySelect key={item.id} value={val || ""} readOnly={true} />
      ),
    },
    {
      title: <SText text={t("sale.address")} />,
      dataIndex: "address",
      width: 200,
      render: (_, item) => (
        <SText text={item.address ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.zipCode")} />,
      dataIndex: "zipCode",
      width: 70,
      render: (_, item) => (
        <SText text={item.zipCode ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.email")} />,
      dataIndex: "email",
      width: 160,
      render: (_, item) => (
        <SText text={item.email ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.contact")} />,
      dataIndex: "contact",
      width: 80,
      render: (_, item) => (
        <SText text={item.contact ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.phone")} />,
      dataIndex: "phone",
      width: 100,
      render: (_, item) => (
        <SText text={item.phone ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.fax")} />,
      dataIndex: "fax",
      width: 100,
      render: (_, item) => (
        <SText text={item.fax ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.mobile")} />,
      dataIndex: "mobile",
      width: 100,
      render: (_, item) => (
        <SText text={item.mobile ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.account1")} />,
      dataIndex: "account1",
      width: 140,
      render: (_, item) => (
        <SText text={item.account1 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.bank1")} />,
      dataIndex: "bank1",
      width: 140,
      render: (_, item) => (
        <SText text={item.bank1 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.swiftCode1")} />,
      dataIndex: "swiftCode1",
      width: 80,
      render: (_, item) => (
        <SText text={item.swiftCode1 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.account2")} />,
      dataIndex: "account2",
      width: 140,
      render: (_, item) => (
        <SText text={item.account2 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.bank2")} />,
      dataIndex: "bank2",
      width: 140,
      render: (_, item) => (
        <SText text={item.bank2 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.swiftCode2")} />,
      dataIndex: "swiftCode2",
      width: 80,
      render: (_, item) => (
        <SText text={item.swiftCode2 ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.currency")} />,
      dataIndex: "currency",
      width: 80,
      render: (val, item) => (
        <CurrencySelect key={item.id} value={val || ""} readOnly={true} />
      ),
    },
    {
      title: <SText text={t("sale.saleType")} />,
      dataIndex: "saleType",
      width: 100,
      render: (val, item) => (
        <Dict type={DictEnum.SALE_TYPE} value={item.saleType} readOnly />
      ),
    },
    {
      title: <SText text={t("sale.creditLevel")} />,
      dataIndex: "creditLevel",
      width: 80,
      render: (val, item) => (
        <CreditLevelSelect value={val} readOnly key={item.id + "creditLevel"} />
      ),
    },
    {
      title: <SText text={t("sale.creditLine")} />,
      dataIndex: "creditLine",
      width: 80,
      render: (_, item) => (
        <SText text={item.creditLine ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.depositRate")} />,
      dataIndex: "depositRate",
      width: 80,
      render: (_, item) => (
        <SText
          text={item.depositRate ?? ""}
          style={{ width: "100%" }}
          suffix="%"
        />
      ),
    },
    {
      title: <SText text={t("sale.tradeTerm")} />,
      dataIndex: "tradeTerm",
      width: 100,
      render: (val, item) => (
        <Dict type={DictEnum.SALE_TRADE_TERM} value={item.tradeTerm} readOnly />
      ),
    },
    {
      title: <SText text={t("sale.payMethod")} />,
      dataIndex: "payMethod",
      width: 100,
      render: (val, item) => (
        <Dict type={DictEnum.SALE_PAY_METHOD} value={item.payMethod} readOnly />
      ),
    },
    {
      title: <SText text={t("sale.commissionRate")} />,
      dataIndex: "commissionRate",
      width: 80,
      render: (_, item) => (
        <SText
          text={item.commissionRate || ""}
          style={{ width: "100%" }}
          suffix="%"
        />
      ),
    },
    {
      title: <SText text={t("sale.affiliates")} />,
      dataIndex: "affiliates",
      width: 140,
      render: (_, item) => (
        <SText text={item.affiliates ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.org")} />,
      dataIndex: "orgId",
      width: 140,
      render: (val, item) => (
        <DepartmentSelect value={val} readOnly style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.billingDays")} />,
      dataIndex: "billingDays",
      width: 80,
      render: (val, item) => (
        <SText text={item.billingDays ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("sale.taxRate")} />,
      dataIndex: "taxRate",
      width: 100,
      render: (_, item) => (
        <SText text={item.taxRate ?? ""} style={{ width: "100%" }} suffix="%" />
      ),
    },
    {
      title: <SText text={t("common.creator")} />,
      dataIndex: "creator",
      width: 100,
      render: (_, item) => {
        let userData = item.creator;
        return (
          userData && <Flex>
            <div>
              <WMSAvatar creator={userData} uniChar={userData.id}
                avatarId={userData.avatar}
                size={24} alt={getUserAvatarTip(userData)}
              />
            </div>
            <SText text={getUserName(userData)} />
          </Flex>
        )
      },
    },
    {
      title: <SText text={t("sale.price")} />,
      dataIndex: "price",
      fixed: "right",
      width: 60,
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => onOpenTab("price", item)}
          />
        </Flex>
      ),
    },
    {
      title: <SText text={t("sale.shipAddress")} />,
      dataIndex: "shipAddress",
      fixed: "right",
      width: 60,
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <Button
            type="primary"
            icon={<EnvironmentOutlined />}
            aria-label={t("sale.shipAddress")}
            onClick={() => onOpenTab("shipAddress", item)}
          />
        </Flex>
      ),
    },
    {
      title: <SText text={t("sale.relatedDocuments")} />,
      dataIndex: "relatedDocuments",
      fixed: "right",
      width: 60,
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={IconFolder}
            alt=""
            onClick={() => onOpenTab("doc", item)}
            style={{ cursor: item.isNew ? "not-allowed" : "pointer" }}
          />
        </Flex>
      ),
    },
    {
      title: t("common.opt"),
      dataIndex: "operation",
      width: 120,
      fixed: "right",
      render: (val, record) => (
        <Flex justify="space-between">
          <Button type="primary" onClick={() => onDetail(record)}>
            {t("common.detail")}
          </Button>
          <Button type="link" danger onClick={() => onDel(record.id)}>
            {t("common.del")}
          </Button>
        </Flex>
      ),
    },
  ], [onDel, onDetail, onOpenTab, t]);

  return columns;
}

export default useCustomerColumn;
